// CustomCalendar.tsx
import { useEffect, useState,useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Stack,
  Paper,
  Button,
  IconButton,
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import api from '../../services/api';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { readDataStorage } from "../../utils/storage/storage";
import { useProgress } from "../../hooks/progress";
import { useAPI } from "../../hooks/contextApis";

dayjs.locale('pt-br');

export default function CustomCalendar() {
  const today = dayjs();
  const [month, setMonth] = useState(today.month());
  const [year, setYear] = useState(today.year());
  const [commitments, setCommitments] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const { setLoad } = useProgress();
  const { setIsOpenUser, setDataUser } = useAPI();
  const { CpfRepresentante } =
    readDataStorage("login");

  const daysOfWeek = ['DOMINGO', 'SEGUNDA', 'TERÇA', 'QUARTA', 'QUINTA', 'SEXTA', 'SÁBADO'];

  useEffect(() => {
    api.get(`/csp/representante/compromissos/consultar?cpf=${CpfRepresentante}`).then((res) => {
      setCommitments(res.data.compromissos || []);
    });
  }, [CpfRepresentante]);

  const handlePrev = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const handleNext = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const getCommitmentsForDay = (date: dayjs.Dayjs) => {
    const datePrefix = date.format('YYYY-MM-DD');
    return commitments.filter((c) => c.dataHoraComp.startsWith(datePrefix));
  };

  const renderCalendar = () => {
    const startOfMonth = dayjs().year(year).month(month).startOf('month');
    const startDay = startOfMonth.day();
    const totalDays = 42;
    const calendarDays = [];

    for (let i = 0; i < totalDays; i++) {
      const day = startOfMonth.subtract(startDay, 'day').add(i, 'day');
      calendarDays.push(day);
    }

    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

    return weeks;
  };

  const weeks = renderCalendar();

  const filteredCommitments = commitments.filter((c) =>
    selectedDate ? c.dataHoraComp.startsWith(selectedDate) : false
  );

  const handleToday = () => {
    const now = dayjs();
    setMonth(now.month());
    setYear(now.year());
    setSelectedDate(now.format('YYYY-MM-DD'));
  };


  const leituraModal = async (idCard: string) => {
    setLoad(true);
    const res = await api.get(`/csp/cliente/consultar/card/${idCard}`);
    const data = res.data;
    setDataUser(data);
    setLoad(false);
    setIsOpenUser(true); // ou setIsOpen(true)
  };

  const visualizarRepresentante = useCallback(
    (idCard) => {
      api.defaults.headers.common = {
        Authorization: `bearer ${sessionStorage.getItem("@ARP-Energia:token")}`,
      };
      api
        .post(`/csp/usuario/alterar/visualizadoRepresentante/`, { idCard })
        .then(() => leituraModal(idCard));
    },
    [leituraModal]
  );

  return (
    <Box p={3} display="flex">
      <Box flex={1}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography fontWeight={700} fontSize={16} color="#5A3DFF" mr={2}>
            {dayjs(`${year}-${month + 1}-01`).format('MMMM YYYY').toUpperCase()}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleToday}
            sx={{
              color: '#6C5AF4',
              borderColor: '#6C5AF4',
              '&:hover': {
                borderColor: '#6C5AF4',
                backgroundColor: '#f2f0ff',
              },
            }}
          >
            Hoje
          </Button>
          <Box flex={1} display="flex" justifyContent="flex-end" gap={1}>
            <IconButton onClick={handlePrev}><ArrowBackIosIcon fontSize="small" /></IconButton>
            <IconButton onClick={handleNext}><ArrowForwardIosIcon fontSize="small" /></IconButton>
          </Box>
        </Box>

        <Grid container columns={7} borderRadius={2} border="1px solid #ddd" sx={{ tableLayout: 'fixed',overflow: 'hidden',}}>
        {daysOfWeek.map((day, index) => (
          <Grid
            item
            xs={1}
            key={day}
            sx={{
              bgcolor: '#ffffff',
              borderBottom: '1px solid #ddd',
              border: '1px solid #ddd',
              height: 40,
              ...(index === 0 && { borderTopLeftRadius: 8 }),
              ...(index === 6 && { borderTopRightRadius: 8 }),
            }}
          >
              <Typography align="center" fontSize={11} fontWeight={700} color="#5A3DFF">
                {day}
              </Typography>
            </Grid>
          ))}

          {weeks.map((week, wIdx) =>
            week.map((date, dIdx) => {
              const fullDate = date.format('YYYY-MM-DD');
              const isSelected = selectedDate === fullDate;
              const isCurrentMonth = date.month() === month;
              const isPast = date.isBefore(today, 'day');
              const isOutside = !isCurrentMonth;

              return (
                <Grid item xs={1} key={`${wIdx}-${dIdx}`} sx={{ border: '1px solid #ddd', height: 110 }}>
                  <Paper
                    variant="outlined"
                    square
                    sx={{
                      height: '100%',
                      p: 0.5,
                      boxShadow: 'none',
                      cursor: 'pointer',
                      bgcolor: isOutside ? '#f9f9f9' : isPast ? '#f5f5f5' : '#ffffff',
                      borderBottomLeftRadius: wIdx === weeks.length - 1 && dIdx === 0 ? 8 : 0,
                      borderBottomRightRadius: wIdx === weeks.length - 1 && dIdx === 6 ? 8 : 0,
                    }}
                    onClick={() => setSelectedDate(fullDate)}
                    onDoubleClick={() => {
                      if (selectedDate === fullDate) {
                        setSelectedDate(null);
                      }
                    }}
                  >
                    <Box display="flex" justifyContent="flex-end" pr={0.5}>
                      <Typography
                        fontSize={12}
                        fontWeight={500}
                        color={isSelected ? 'white' : isOutside ? '#bdbdbd' : isPast ? '#9e9e9e' : 'text.secondary'}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: isSelected ? '#5A3DFF' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {date.date()}
                      </Typography>
                    </Box>

                    <Stack spacing={0.3} mt={0.5}>
                      <Box   sx={{
                          overflowY: 'auto',
                          pr: 0.5,
                          scrollbarWidth: 'none', 
                          '&::-webkit-scrollbar': {
                            display: 'none', 
                          },
                        }}>
                          {(() => {
                            const dailyCommitments = [...getCommitmentsForDay(date)].sort(
                              (a, b) => dayjs(a.dataHoraComp).unix() - dayjs(b.dataHoraComp).unix()
                            );

                            const firstTwo = dailyCommitments.slice(0, 2);
                            const remaining = dailyCommitments.length - firstTwo.length;

                            return (
                              <>
                                {firstTwo.map((c) => (
                                  <Box
                                    key={c.id}
                                    sx={{
                                      fontSize: '10px',
                                      px: 0.5,
                                      py: 0.2,
                                      border: '1px solid #5A3DFF',
                                      borderRadius: '6px',
                                      color: '#5A3DFF',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      backgroundColor: 'white',
                                      mb: 0.3,
                                      textAlign:'center'
                                    }}
                                  >
                                  <Typography component="span" fontWeight={600} fontSize="10px" color="#5A3DFF">
                                      {dayjs(c.dataHoraComp).format('HH:mm')}
                                    </Typography>{' '}
                                    <Typography component="span" fontSize="10px" color="black">
                                      ID {c.idCard}
                                    </Typography>
                                  </Box>
                                ))}
                                {remaining > 0 && (
                                  <Box
                                    sx={{
                                      fontSize: '10px',
                                      px: 0.5,
                                      py: 0.2,
                                      border: '1px solid #5A3DFF',
                                      borderRadius: '6px',
                                      color: '#000',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      backgroundColor: 'white',
                                      textAlign:'center'
                                    }}
                                  >
                                    + {remaining} compromissos
                                  </Box>
                                )}
                              </>
                            );
                          })()}
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              );
            })
          )}
        </Grid>
      </Box>

      {selectedDate && (
        <Box ml={3}> 
          <Typography fontWeight={700} fontSize={14} color="#5A3DFF" mb={1}>
            {dayjs(selectedDate).format('D [de] MMMM [de] YYYY')}
          </Typography>
          <Stack spacing={1}>
            {filteredCommitments.sort((a, b) => dayjs(a.dataHoraComp).unix() - dayjs(b.dataHoraComp).unix()).map((ev) => (
              <Box
                key={ev.id}
                border="1px solid #5A3DFF"
                borderRadius={1}
                p={1.2}
                sx={{ cursor: 'pointer' }}
                onClick={() => visualizarRepresentante(ev.idCard)}
              >
                <Typography fontSize={12} fontWeight={600}>
                  <Box component="span" color="#5A3DFF">
                    {dayjs(ev.dataHoraComp).format('HH:mm')}
                  </Box>{' '}
                  — ID{' '}
                  <Box component="span" color="black">
                    {ev.idCard}
                  </Box>
                </Typography>
                <Typography fontSize={12}>
                  {ev.descricao}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}